import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { toSearchParams, type TimeRange } from "@/lib/time-range";
import {
  Ga4OverviewSchema, type Ga4Overview,
  Ga4BreakdownSchema, type Ga4Breakdown,
  Ga4EventsSchema, type Ga4Events,
  Ga4KeyEventsSchema, type Ga4KeyEvents,
  Ga4PagesSchema, type Ga4Pages,
  Ga4TrendSchema, type Ga4Trend,
  Ga4PeakSchema, type Ga4Peak,
} from "@/lib/validation";

export type Ga4Device = "desktop" | "mobile" | "tablet";

export interface UseGa4Options {
  enabled?: boolean;
  /** FR-09 segment filter — restricts the report to one device category. */
  device?: Ga4Device | null;
}

/** GA4 endpoints take only from/to; toSearchParams adds granularity which the
 *  backend ignores. staleTime is longer (GA4 is daily, not real-time). */
function useGa4Query<T>(
  path: string,
  key: readonly unknown[],
  parse: (raw: unknown) => T,
  range: TimeRange,
  options: UseGa4Options,
) {
  const device = options.device ?? null;
  return useQuery<T>({
    queryKey: device ? [...key, { device }] : key,
    queryFn: async ({ signal }) => {
      const search = toSearchParams(range);
      if (device) search.set("device", device);
      return parse(await api.get<unknown>(`${path}?${search.toString()}`, { signal }));
    },
    enabled: options.enabled !== false,
    staleTime: 5 * 60_000,
  });
}

export const useGa4Overview = (r: TimeRange, o: UseGa4Options = {}) =>
  useGa4Query<Ga4Overview>("/api/v1/ga4/overview", ["ga4", "overview", r], (x) => Ga4OverviewSchema.parse(x), r, o);

export const useGa4Country = (r: TimeRange, o: UseGa4Options = {}) =>
  useGa4Query<Ga4Breakdown>("/api/v1/ga4/by-country", ["ga4", "country", r], (x) => Ga4BreakdownSchema.parse(x), r, o);

export const useGa4Device = (r: TimeRange, o: UseGa4Options = {}) =>
  useGa4Query<Ga4Breakdown>("/api/v1/ga4/by-device", ["ga4", "device", r], (x) => Ga4BreakdownSchema.parse(x), r, o);

export const useGa4Channel = (r: TimeRange, o: UseGa4Options = {}) =>
  useGa4Query<Ga4Breakdown>("/api/v1/ga4/by-channel", ["ga4", "channel", r], (x) => Ga4BreakdownSchema.parse(x), r, o);

export const useGa4TopEvents = (r: TimeRange, o: UseGa4Options = {}) =>
  useGa4Query<Ga4Events>("/api/v1/ga4/top-events", ["ga4", "top-events", r], (x) => Ga4EventsSchema.parse(x), r, o);

export const useGa4KeyEvents = (r: TimeRange, o: UseGa4Options = {}) =>
  useGa4Query<Ga4KeyEvents>("/api/v1/ga4/key-events", ["ga4", "key-events", r], (x) => Ga4KeyEventsSchema.parse(x), r, o);

export const useGa4TopPages = (r: TimeRange, o: UseGa4Options = {}) =>
  useGa4Query<Ga4Pages>("/api/v1/ga4/top-pages", ["ga4", "top-pages", r], (x) => Ga4PagesSchema.parse(x), r, o);

export const useGa4Trend = (r: TimeRange, o: UseGa4Options = {}) =>
  useGa4Query<Ga4Trend>("/api/v1/ga4/trend", ["ga4", "trend", r], (x) => Ga4TrendSchema.parse(x), r, o);

export const useGa4ByHour = (r: TimeRange, o: UseGa4Options = {}) =>
  useGa4Query<Ga4Peak>("/api/v1/ga4/by-hour", ["ga4", "by-hour", r], (x) => Ga4PeakSchema.parse(x), r, o);

export const useGa4ByDayOfWeek = (r: TimeRange, o: UseGa4Options = {}) =>
  useGa4Query<Ga4Peak>("/api/v1/ga4/by-day-of-week", ["ga4", "by-dow", r], (x) => Ga4PeakSchema.parse(x), r, o);

import { Ga4RealtimeSchema, type Ga4Realtime } from "@/lib/validation";

/** Realtime active users — refetches every 30s, no time range. */
export function useGa4Realtime(options: UseGa4Options = {}) {
  return useQuery<Ga4Realtime>({
    queryKey: ["ga4", "realtime"],
    queryFn: async ({ signal }) =>
      Ga4RealtimeSchema.parse(await api.get<unknown>("/api/v1/ga4/realtime", { signal })),
    enabled: options.enabled !== false,
    staleTime: 25_000,
    refetchInterval: 30_000,
  });
}
